import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import * as styles from './Nav.module.css';
import { NavItem, NavProps } from './types';

interface SubMenuProps {
  item: NavItem;
  isItemActive: (item: NavItem) => boolean;
  level?: number;
}

const SubMenu: React.FC<SubMenuProps> = ({ item, isItemActive, level = 0 }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const closeTimerRef = React.useRef<number | null>(null);
  const hasChildren = Boolean(item.children?.length);
  const isActive = isItemActive(item);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleEnter = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, 180);
  };

  if (!hasChildren) {
    return (
      <Link
        to={item.to || '#'}
        className={`${styles.menuItem} ${isActive ? styles.active : ''}`}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div
      className={styles.submenuGroup}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className={styles.submenuTitle}>{item.label}</div>
      {isOpen && (
        <div
          className={`${styles.menu} ${level > 0 ? styles.menuVertical : styles.menuHorizontal}`}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          {item.children!.map((child, idx) => (
            <SubMenu
              key={child.id ?? idx}
              item={child}
              isItemActive={isItemActive}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Nav: React.FC<NavProps> = ({ items }) => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const location = useLocation();

  const isItemActive = (item: NavItem): boolean => {
    if (item.to && location.pathname === item.to) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => isItemActive(child));
    }
    return false;
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleEnter = (idx: number) => {
    clearCloseTimer();
    setOpenIndex(idx);
  };

  const handleLeave = (idx: number) => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenIndex(prev => (prev === idx ? null : prev));
      closeTimerRef.current = null;
    }, 180);
  };

  return (
    <nav className={styles.root}>
      {items.map((item, idx) => {
        const hasChildren = Boolean(item.children?.length);
        if (!hasChildren) {
          return (
            <NavLink
              key={item.id ?? idx}
              to={item.to || '#'}
              className={({ isActive }) =>
                `${styles.link} ${isActive ? styles.active : ''}`
              }
            >
              {item.label}
            </NavLink>
          );
        }

        const isOpen = openIndex === idx;
        const isActive = isItemActive(item);
        return (
          <div
            key={item.id ?? idx}
            className={styles.group}
            onMouseEnter={() => handleEnter(idx)}
            onMouseLeave={() => handleLeave(idx)}
          >
            <button
              className={`${styles.link} ${isActive ? styles.active : ''}`}
              type="button"
              aria-expanded={isOpen}
            >
              {item.label}
            </button>
            {isOpen && (
              <div
                className={`${styles.menu} ${styles.menuHorizontal}`}
                onMouseEnter={() => handleEnter(idx)}
                onMouseLeave={() => handleLeave(idx)}
              >
                {item.children!.map((child, ci) => {
                  const hasSubChildren = Boolean(child.children?.length);
                  const isChildActive = isItemActive(child);

                  if (!hasSubChildren) {
                    return (
                      <Link
                        key={child.id ?? ci}
                        to={child.to || '#'}
                        className={`${styles.menuItem} ${isChildActive ? styles.active : ''}`}
                      >
                        {child.label}
                      </Link>
                    );
                  }

                  return (
                    <div key={child.id ?? ci} className={styles.submenuGroup}>
                      <div className={styles.submenuTitle}>{child.label}</div>
                      {child.children!.map((subItem, subIdx) => {
                        const isSubItemActive = isItemActive(subItem);
                        return (
                          <Link
                            key={subItem.id ?? subIdx}
                            to={subItem.to || '#'}
                            className={`${styles.menuItem} ${isSubItemActive ? styles.active : ''}`}
                          >
                            {subItem.label}
                          </Link>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default Nav;
